import Shell from '../components/Shell';
import Seo from '../components/Seo';
import { PageHero, Section, CTA } from '../components/Pieces';
import { villageJourney, classroomModes, siteImages } from '../mock';
import { WifiOff, Server, Laptop, GraduationCap } from 'lucide-react';

export default function VillageSchools() {
  return (
    <Shell>
      <Seo title="Village School Transformation"
        description="Infrastructure-light digital learning for village schools: a learning centre, a smartphone, a screen and an offline-first content pipeline."
        image={siteImages.villageSchools} />
      <PageHero
        kicker="Leapfrog Learning for Village Schools"
        title="A learning centre. A smartphone. A screen. A pathway to connected education."
        sub="Lightweight infrastructure activating a much richer learning ecosystem — designed offline-first for real ground realities."
        breadcrumbs={[{ label: 'Transform Education' }, { label: 'Village Schools' }]}
        image={siteImages.villageSchools}
        imageAlt="Children learning together in a village school classroom"
      />

      <Section bg="white">
        <div className="text-center max-w-2xl mx-auto">
          <div className="mm-kicker mb-3">The Seven-Step Journey</div>
          <h2 className="font-display text-3xl md:text-5xl font-bold text-[var(--mm-ink)] mm-rule-center">From onboarding to evidence-led adaptation.</h2>
        </div>
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {villageJourney.map((j) => (
            <div key={j.n} className="mm-card p-6">
              <div className="w-12 h-12 rounded-full bg-[var(--mm-purple)] text-white flex items-center justify-center font-display font-bold">{j.n}</div>
              <h3 className="mt-4 font-display text-lg font-bold text-[var(--mm-ink)]">{j.title}</h3>
              <p className="mt-2 text-sm text-[var(--mm-ink)]/70 leading-relaxed">{j.body}</p>
            </div>
          ))}
        </div>
        <p className="mt-10 text-center text-[var(--mm-ink)]/70 italic">Evidence → Updated Learning Path → Next Classroom Experience.</p>
      </Section>

      <Section bg="cream2">
        <div className="grid md:grid-cols-2 gap-8">
          <div className="mm-card p-8">
            <WifiOff className="w-8 h-8 text-[var(--mm-purple)]"/>
            <h3 className="mt-4 font-display text-2xl font-bold text-[var(--mm-ink)]">Offline-first by design</h3>
            <p className="mt-3 text-[var(--mm-ink)]/70">Pre-approved lessons and learning assets available locally even when daily internet or a specialist teacher is unavailable.</p>
          </div>
          <div className="mm-card p-8">
            <Server className="w-8 h-8 text-[var(--mm-purple)]"/>
            <h3 className="mt-4 font-display text-2xl font-bold text-[var(--mm-ink)]">Infrastructure-light</h3>
            <p className="mt-3 text-[var(--mm-ink)]/70">A smartphone, a screen and a resilient content pipeline — enough to activate a functioning digital classroom.</p>
          </div>
        </div>
      </Section>

      <CTA title="Activate a village learning centre." primary={{ label: 'Talk to us', href: '/contact' }} />
    </Shell>
  );
}

export function RemoteClassrooms() {
  return (
    <Shell>
      <Seo title="Remote & Autonomous Classrooms"
        description="Four classroom modes — connected, autonomous, edge-enabled and remote — that bring approved lessons to any environment."
        image={siteImages.remoteClassrooms} />
      <PageHero
        kicker="Remote & Autonomous Classrooms"
        title="Teach beyond the walls. Learn beyond the schedule."
        sub="Four classroom modes designed to bring learning to any environment — connected, autonomous, edge-enabled or remote."
        breadcrumbs={[{ label: 'Transform Education' }, { label: 'Remote Classrooms' }]}
        image={siteImages.remoteClassrooms}
        imageAlt="Students watching a lesson on a classroom screen"
      />
      <Section bg="white">
        <div className="grid md:grid-cols-2 gap-5">
          {classroomModes.map((m, i) => {
            const Icon = [GraduationCap, WifiOff, Server, Laptop][i];
            return (
              <div key={m.title} className="mm-card p-8">
                <Icon className="w-8 h-8 text-[var(--mm-purple)]"/>
                <h3 className="mt-4 font-display text-xl font-bold text-[var(--mm-ink)]">{m.title}</h3>
                <p className="mt-2 text-[var(--mm-ink)]/70 leading-relaxed">{m.body}</p>
              </div>
            );
          })}
        </div>
        <p className="mt-8 text-sm text-[var(--mm-ink)]/60">Mashmari’s remote-classroom architecture engages with the ITU-T Y.4234 direction of work on connected/autonomous classrooms.</p>
      </Section>
      <CTA title="Design a classroom that meets your context." />
    </Shell>
  );
}
